/**
 * Utilidades para trabajar con tokens JWT
 * Nota: Solo decodifica el payload para leer información, NO verifica la firma
 */

interface JWTPayload {
  exp?: number;
  iat?: number;
  [key: string]: any;
}

/**
 * Decodifica un token JWT sin verificar la firma
 * Solo para leer información como la expiración
 */
export const decodeJWT = (token: string): JWTPayload | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decodificando JWT:', error);
    return null;
  }
};

/**
 * Verifica si un token JWT está expirado
 */
export const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;

  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return true;

  // exp está en segundos, Date.now() está en milisegundos
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();

  return currentTime >= expirationTime;
};

/**
 * Obtiene el tiempo restante hasta la expiración del token en milisegundos
 * Retorna 0 si está expirado o no hay token
 */
export const getTokenTimeRemaining = (token: string | null): number => {
  if (!token) return 0;

  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return 0;

  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  const remaining = expirationTime - currentTime;

  return remaining > 0 ? remaining : 0;
};

/**
 * Verifica si un token está próximo a expirar (menos de X minutos)
 */
export const isTokenExpiringSoon = (
  token: string | null,
  minutesThreshold: number = 5
): boolean => {
  const timeRemaining = getTokenTimeRemaining(token);
  const thresholdMs = minutesThreshold * 60 * 1000;
  return timeRemaining > 0 && timeRemaining < thresholdMs;
};

/**
 * Obtiene información del token (expiración, tiempo restante, etc.)
 */
export const getTokenInfo = (token: string | null) => {
  if (!token) {
    return {
      isValid: false,
      isExpired: true,
      expiresAt: null,
      timeRemaining: 0,
      timeRemainingMinutes: 0,
    };
  }

  const payload = decodeJWT(token);
  const isExpired = isTokenExpired(token);
  const timeRemaining = getTokenTimeRemaining(token);

  return {
    isValid: payload !== null && !isExpired,
    isExpired,
    expiresAt: payload?.exp ? new Date(payload.exp * 1000) : null,
    timeRemaining,
    timeRemainingMinutes: Math.floor(timeRemaining / (60 * 1000)),
    payload,
  };
};

